"use client"

import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Navbar, Footer } from "@/components/index"
import {
  BlogHero,
  BlogSearch,
  TopicFilter,
  BlogGrid,
  EmptyBlogState,
  BlogListSkeleton,
  BlogCardSkeleton,
} from "@/components/index"
import { useDebounce } from "@/hooks/useDebounce"
import {
  getPublishedPosts,
  getAllTags,
  searchPosts,
} from "@/lib/supabase/queries"
import type { TopicTag } from "@/types/blog"

export function BlogPageClient() {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null)

  const debouncedQuery = useDebounce(searchQuery, 300)

  // Fetch tags
  const { data: tags = [] } = useQuery<TopicTag[]>({
    queryKey: ["blog-tags"],
    queryFn: getAllTags,
  })

  // Fetch posts - switches between all posts and search results
  const {
    data: posts = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["blog-posts", debouncedQuery, selectedTopic],
    queryFn: () => {
      if (debouncedQuery || selectedTopic) {
        return searchPosts(debouncedQuery, selectedTopic)
      }
      return getPublishedPosts()
    },
  })

  const handleTopicClick = (topicSlug: string | null) => {
    setSelectedTopic(topicSlug)
  }

  const clearFilters = () => {
    setSearchQuery("")
    setSelectedTopic(null)
  }

  const hasFilters = !!searchQuery || !!selectedTopic

  if (isLoading) {
    return <BlogListSkeleton />
  }

  return (
    <div className="min-h-screen bg-bg">
      <Navbar />

      {/* Hero Section */}
      <BlogHero
        title="Blog"
        description="Insights, updates, and deep dives into spec-driven development, AI collaboration, and modern software engineering."
      />

      {/* Search & Filters */}
      <section className="py-6 sm:py-8">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search */}
          <div className="mb-4 sm:mb-6">
            <BlogSearch
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search articles..."
              className="max-w-xl"
            />
          </div>

          {/* Topic Filter */}
          <TopicFilter
            tags={tags}
            selectedTopic={selectedTopic}
            onSelectTopic={handleTopicClick}
          />
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="pb-16 sm:pb-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          {isFetching && !isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <BlogCardSkeleton key={i} />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <EmptyBlogState
              hasFilters={hasFilters}
              onClearFilters={clearFilters}
            />
          ) : (
            <BlogGrid posts={posts} />
          )}
        </div>
      </section>

      <Footer />
    </div>
  )
}
